import { useState } from "react";
import { MODAL_STYLES } from "../../constants/modal-styles";
import { ModalButton } from "./ModalButton";

export type Gender = "male" | "female";

export interface AcquaintanceFormData {
  age: number;
  instagram: string;
  text: string;
  gender: Gender;
}

interface LeaveAcquaintanceFormModalProps {
  isOpen: boolean;
  language: "ka" | "en";
  onSave: (data: AcquaintanceFormData) => void;
  onCancel: () => void;
}

export const LeaveAcquaintanceFormModal = ({
  isOpen,
  language,
  onSave,
  onCancel,
}: LeaveAcquaintanceFormModalProps) => {
  const [age, setAge] = useState("");
  const [instagram, setInstagram] = useState("");
  const [text, setText] = useState("");
  const [gender, setGender] = useState<Gender | null>(null);

  if (!isOpen) return null;

  const content = {
    ka: {
      title: "ახალი ზნაკომstvo",
      subtitle: "შეავსეთ ინფორმაცია",
      ageLabel: "თქვენი ასაკი:",
      agePlaceholder: "მაგ: 25",
      instaLabel: "Instagram:",
      instaPlaceholder: "@username",
      textLabel: "შეტყობინება:",
      textPlaceholder: "დაწერეთ რამე საკუთარ თავზე...",
      genderLabel: "თქვენი სქესი:",
      male: "მამაკაცი",
      female: "ქალი",
      saveButton: "შემდეგი (აირჩიეთ ადგილი)",
      cancelButton: "გაუქმება",
      errorAge: "გთხოვთ შეიყვანოთ ასაკი (18-100)",
      errorInsta: "გთხოვთ შეიყვანოთ Instagram",
      errorText: "გთხოვთ დაწეროთ ტექსტი",
      errorGender: "გთხოვთ აირჩიოთ სქესი",
    },
    en: {
      title: "New Acquaintance",
      subtitle: "Fill in the information",
      ageLabel: "Your age:",
      agePlaceholder: "e.g: 25",
      instaLabel: "Instagram:",
      instaPlaceholder: "@username",
      textLabel: "Message:",
      textPlaceholder: "Write something about yourself...",
      genderLabel: "Your gender:",
      male: "Male",
      female: "Female",
      saveButton: "Next (Choose location)",
      cancelButton: "Cancel",
      errorAge: "Please enter age (18-100)",
      errorInsta: "Please enter Instagram",
      errorText: "Please write text",
      errorGender: "Please select gender",
    },
  };

  const translations = content[language] || content.ka;

  const handleSave = () => {
    const ageNum = parseInt(age);

    if (!age || isNaN(ageNum) || ageNum < 18 || ageNum > 100) {
      alert(translations.errorAge);
      return;
    }

    if (!instagram.trim()) {
      alert(translations.errorInsta);
      return;
    }

    if (!text.trim()) {
      alert(translations.errorText);
      return;
    }

    if (!gender) {
      alert(translations.errorGender);
      return;
    }

    onSave({
      age: ageNum,
      instagram: instagram.trim(),
      text: text.trim(),
      gender,
    });

    // Сброс формы
    setAge("");
    setInstagram("");
    setText("");
    setGender(null);
  };

  const handleCancel = () => {
    setAge("");
    setInstagram("");
    setText("");
    setGender(null);
    onCancel();
  };

  return (
    <div className={MODAL_STYLES.overlay} onClick={handleCancel}>
      <div
        className={`${MODAL_STYLES.modal} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: MODAL_STYLES.fontFamily,
          maxWidth: "500px",
        }}
      >
        <div className="px-4 py-4 sm:px-8 sm:py-6">
          <h2 className="text-lg sm:text-2xl font-bold text-amber-900 mb-3 sm:mb-6 text-center">{translations.title}</h2>

          <p className="text-amber-900 text-sm sm:text-base mb-3 sm:mb-6 text-center">
            {translations.subtitle}
          </p>

          {/* Возраст */}
          <div className="mb-3 sm:mb-4">
            <label className="block text-amber-900 font-bold mb-2 text-sm">
              {translations.ageLabel}
            </label>
            <input
              type="number"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder={translations.agePlaceholder}
              min="18"
              max="100"
              className="w-full p-2 sm:p-3 bg-white border-2 border-amber-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-amber-900 text-sm sm:text-base"
              style={{ fontFamily: MODAL_STYLES.fontFamily }}
            />
          </div>

          {/* Instagram */}
          <div className="mb-3 sm:mb-4">
            <label className="block text-amber-900 font-bold mb-2 text-sm">
              {translations.instaLabel}
            </label>
            <input
              type="text"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder={translations.instaPlaceholder}
              className="w-full p-2 sm:p-3 bg-white border-2 border-amber-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-amber-900 text-sm sm:text-base"
              style={{ fontFamily: MODAL_STYLES.fontFamily }}
            />
          </div>

          {/* Текст */}
          <div className="mb-3 sm:mb-4">
            <label className="block text-amber-900 font-bold mb-2 text-sm">
              {translations.textLabel}
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={translations.textPlaceholder}
              className="w-full h-20 sm:h-24 p-2 sm:p-3 bg-white border-2 border-amber-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-amber-500 text-amber-900 text-sm sm:text-base"
              style={{ fontFamily: MODAL_STYLES.fontFamily }}
            />
          </div>

          {/* Выбор пола */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-amber-900 font-bold mb-3 text-sm">
              {translations.genderLabel}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setGender("male")}
                className={`flex flex-col items-center p-2 sm:p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                  gender === "male"
                    ? "bg-amber-300 border-amber-800 shadow-lg"
                    : "bg-amber-100/80 border-amber-600 hover:bg-amber-200/90"
                }`}
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 mb-1 sm:mb-2 flex items-center justify-center">
                  <img
                    src="/models/treasure-hunt/man-sand.png"
                    alt="Male"
                    className="max-w-full max-h-full object-contain"
                    style={{ imageRendering: "pixelated" }}
                  />
                </div>
                <span className="text-amber-900 font-bold text-sm">
                  {translations.male}
                </span>
              </button>

              <button
                onClick={() => setGender("female")}
                className={`flex flex-col items-center p-2 sm:p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                  gender === "female"
                    ? "bg-amber-300 border-amber-800 shadow-lg"
                    : "bg-amber-100/80 border-amber-600 hover:bg-amber-200/90"
                }`}
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 mb-1 sm:mb-2 flex items-center justify-center">
                  <img
                    src="/models/treasure-hunt/woman-sand-lies2.png"
                    alt="Female"
                    className="max-w-full max-h-full object-contain"
                    style={{ imageRendering: "pixelated" }}
                  />
                </div>
                <span className="text-amber-900 font-bold text-sm">
                  {translations.female}
                </span>
              </button>
            </div>
          </div>

          {/* Кнопки */}
          <div className={MODAL_STYLES.buttonContainer}>
            <ModalButton onClick={handleSave} fullWidth={true}>
              <span className="text-sm sm:text-base">{translations.saveButton}</span>
            </ModalButton>

            <ModalButton onClick={handleCancel} variant="secondary">
              <span className="text-sm sm:text-base">{translations.cancelButton}</span>
            </ModalButton>
          </div>
        </div>
      </div>
    </div>
  );
};
